import { useEffect, useState } from "react";
import { Box, Button, Flex, HStack, Spinner, Text } from "@chakra-ui/react";
import download from "downloadjs";
import { fileTypeFromBuffer } from "file-type";
import { useApi } from "../../../hooks/useApi";
import { FileType } from "../../../types";
import { destructureDocPath } from "../../../util/docPath";
import Viewer from "../Viewer";
import { kindFor, PreviewKind } from "./parsers";
import { SheetPreview, ZipPreview } from "./BinaryRenderers";
import { CsvPreview, EdiPreview, TextPreview, XmlPreview } from "./Renderers";

type Loaded = { blob: Blob; bytes: Uint8Array; name: string; kind: PreviewKind; text: string | null };

// Read-only preview of one document, opened from the document library. The metadata editor
// (file_viewer) stays separate: this page never writes anything.
export default function Preview({ id }: { id: string }) {
  const { getFile, getDocByHash, downloadFile } = useApi();
  const [file, setFile] = useState<FileType>();
  const [loaded, setLoaded] = useState<Loaded>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const previous = document.title;
    document.title = "Preview";
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // A 32-hex id is a filehash (from the Upload Log): any file type in the hash library, not
        // only filed documents. Otherwise it is a document's sid (from the Document Library).
        const hashId = /^[0-9a-f]{32}$/i.test(id) ? id : undefined;
        const row = hashId ? ((await getDocByHash(hashId)) as unknown as FileType | null) : await getFile(id);
        if (!row) throw new Error("no such file");
        if (cancelled) return;
        setFile(row);
        const { drive, dir, filename } = destructureDocPath(String(row.doc_path).replace(/\.txt$/i, ""));
        const bytes = await downloadFile(drive, dir, filename);
        const kind = kindFor(filename);
        const detected = await fileTypeFromBuffer(bytes);
        const isText = kind === "csv" || kind === "edi" || kind === "xml" || kind === "text";
        if (cancelled) return;
        setLoaded({
          bytes,
          name: filename,
          kind,
          blob: new Blob([bytes], { type: detected?.mime }),
          text: isText ? new TextDecoder("utf-8").decode(bytes) : null,
        });
      } catch (e) {
        console.error("Preview failed", e);
        if (!cancelled) setError("Could not load this document.");
      }
    })();
    return () => { cancelled = true; };
  }, [id, getFile, getDocByHash, downloadFile]);

  if (error) return <Text p={8} color="red.500">{error}</Text>;
  if (!loaded) return <Flex h="100vh" align="center" justify="center"><Spinner size="xl" /></Flex>;

  const { kind, text, blob, name } = loaded;
  const saveAs = file?.eb_filename?.trim() || name;

  return (
    <Flex direction="column" h="100vh">
      <HStack px={4} py={2} borderBottom="1px solid" borderColor="gray.200" justify="space-between">
        <Text fontWeight="semibold" noOfLines={1}>{saveAs}</Text>
        <Button size="sm" onClick={() => download(blob, saveAs)}>Download</Button>
      </HStack>
      <Box flex="1" minH={0}>
        {(kind === "pdf" || kind === "image") && <Viewer fileBlob={blob} fileName={name} />}
        {kind === "sheet" && <SheetPreview bytes={loaded.bytes} />}
        {kind === "zip" && <ZipPreview bytes={loaded.bytes} />}
        {kind === "csv" && text !== null && <CsvPreview text={text} />}
        {kind === "edi" && text !== null && <EdiPreview text={text} />}
        {kind === "xml" && text !== null && <XmlPreview text={text} />}
        {kind === "text" && text !== null && <TextPreview text={text} />}
        {kind === "unsupported" && (
          <Flex h="100%" align="center" justify="center" direction="column" gap={2}>
            <Text>No preview is available for .{name.split(".").pop()} files yet.</Text>
            <Button onClick={() => download(blob, saveAs)}>Download instead</Button>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}
